import React, { useState } from 'react';

import { ButtonBase, Collapse, List, makeStyles, Typography } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import useLayoutStore from 'storages/layoutStore';

const MENU_TEXT_MUTED = 'rgba(248, 251, 255, 0.72)';

const useStyles = makeStyles(theme => ({
  section: {
    transition: '.2s',
    padding: ({ isMenuOpen }) => theme.spacing(isMenuOpen ? 1.5 : 1),
    display: ({ isMenuOpen, keepVisible }) => (isMenuOpen || keepVisible ? 'block' : 'none'),
    borderBottom: ({ divider }) => divider && `1px solid ${theme.palette.accents.separator}`,
    color: '#F8FBFF',
    background: 'transparent',
    [theme.breakpoints.down('sm')]: {
      padding: () => theme.spacing(1),
    },
  },
  sectionTitleButton: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 30px',
    borderRadius: theme.shape.borderRadius,
    color: MENU_TEXT_MUTED,
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
      },
    },
    '&.Mui-focusVisible, &:focus-visible': {
      outline: `3px solid ${theme.palette.grey[300]}`,
      outlineOffset: 2,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  title: {
    fontSize: '.75rem',
    textTransform: 'uppercase',
    padding: theme.spacing(1, 1.5),
    fontWeight: 800,
    letterSpacing: '.08em',
    textAlign: 'left',
    color: MENU_TEXT_MUTED,

    [theme.breakpoints.down('sm')]: {
      fontSize: '1.125rem',
    },
  },
  chevron: {
    transition: '.2s',
    transform: ({ isSectionOpen }) => (isSectionOpen ? 'rotate(180deg)' : 'rotate(0)'),
  },
}));

const MenuSection = ({ children, className, title, divider, collapsible, keepVisible, ...props }) => {
  const [isSectionOpen, setIsSectionOpen] = useState(!collapsible);
  const toggle = () => setIsSectionOpen(prev => !prev);
  const { isMenuOpen } = useLayoutStore();

  const styles = useStyles({ isMenuOpen, divider, keepVisible, isSectionOpen });

  if (!children) return null;
  if (!collapsible) {
    return (
      <div className={clsx(styles.section, className)} {...props}>
        {title && (
          <Typography className={styles.title} component='h4'>
            {title}
          </Typography>
        )}
        {children}
      </div>
    );
  }
  return (
    <List className={clsx(styles.section, className)} {...props}>
      {title && (
        <ButtonBase className={styles.sectionTitleButton} disableRipple onClick={toggle}>
          <Typography className={styles.title} component='h4'>
            {title}
          </Typography>
          <KeyboardArrowDownIcon className={styles.chevron} />
        </ButtonBase>
      )}
      <Collapse in={isSectionOpen} timeout='auto'>
        {children}
      </Collapse>
    </List>
  );
};

MenuSection.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  title: PropTypes.string,
  divider: PropTypes.bool,
  collapsible: PropTypes.bool,
  keepVisible: PropTypes.bool,
};

MenuSection.defaultProps = {
  className: null,
  title: null,
  divider: true,
  collapsible: true,
  keepVisible: false,
};

export default MenuSection;
